import { LRUCache } from 'lru-cache';

export type SizeEstimator<V> = (value: V) => number;

type SetOpts = {
  autoPin?: boolean;
};

/**
 * A Least Recently Used (LRU) cache with support for pinning entries to prevent their eviction until unpinned.
 */
export class PinnedLRUCache<K extends {}, V extends {}> {
  private cache: LRUCache<K, V>;
  private pins = new Map<K, number>();

  constructor(
    private readonly maxSize: number,
    private readonly estimateSize: SizeEstimator<V>,
  ) {
    this.cache = new LRUCache<K, V>({
      maxSize: Number.MAX_SAFE_INTEGER, // disable internal eviction, we handle it ourselves
      sizeCalculation: estimateSize,
    });
  }

  get(key: K): V | undefined {
    return this.cache.get(key);
  }

  set(key: K, value: V, opts?: SetOpts): void {
    const size = this.estimateSize(value);

    // If replacing, remove old entry first so size math is correct
    if (this.cache.has(key)) {
      this.cache.delete(key);
    }

    this._evictEntries(size);

    // Always insert (even if still overflowing because everything is pinned)
    this.cache.set(key, value);

    if (opts?.autoPin) {
      this.pin(key);
    }
  }

  /**
   * Batch set multiple entries at once, evicting as needed. More efficient since entries are scanned for eviction only once.
   */
  batchSet(entries: Array<[K, V]>, opts?: SetOpts): void {
    const size = entries.reduce(
      (acc, [, value]) => acc + this.estimateSize(value),
      0,
    );
    this._evictEntries(size);
    entries.forEach(([key, value]) => this.set(key, value, opts));
  }

  delete(key: K): void {
    this.cache.delete(key);
    this.pins.delete(key);
  }

  pin(key: K): void {
    if (!this.cache.has(key)) {
      throw new Error(`Cannot pin missing key: ${String(key)}`);
    }
    this.pins.set(key, (this.pins.get(key) ?? 0) + 1);
  }

  unpin(key: K): void {
    const n = this.pins.get(key);
    if (!n) return;

    if (n === 1) this.pins.delete(key);
    else this.pins.set(key, n - 1);
  }

  has(key: K): boolean {
    return this.cache.has(key);
  }

  size(): number {
    return this.cache.size;
  }

  totalSize(): number {
    return this.cache.calculatedSize;
  }

  keysLRU(): K[] {
    return [...this.cache.rkeys()]; // LRU -> MRU
  }

  keysMRU(): K[] {
    return [...this.cache.keys()]; // MRU -> LRU
  }

  private _evictEntries(extraSize?: number): void {
    // Evict LRU entries until it fits (skip pinned)
    const iter = this.cache.rkeys(); // LRU -> MRU
    for (let next = iter.next(); !next.done; next = iter.next()) {
      if (this.cache.calculatedSize + (extraSize ?? 0) <= this.maxSize) break;
      const k = next.value;
      if (!this.pins.has(k)) {
        this.cache.delete(k);
      }
    }
  }
}
